/**
 * DrilldownModal Component - Shows transaction details when clicking on chart
 */
import React from 'react';
import type { Transaction } from './types';

interface DrilldownModalProps {
  visible: boolean;
  date: string | null;
  transactions: Transaction[];
  onClose: () => void;
  loading?: boolean;
}

const typeColors = {
  bank: 'bg-green-500/20 text-green-400',
  crypto: 'bg-blue-500/20 text-blue-400',
  commission: 'bg-purple-500/20 text-purple-400',
  conversion: 'bg-amber-500/20 text-amber-400',
};

const typeLabels = {
  bank: 'Bank Transfer',
  crypto: 'Crypto Payment',
  commission: 'Commission',
  conversion: 'USDT Conversion',
};

export const DrilldownModal: React.FC<DrilldownModalProps> = ({
  visible, date, transactions, onClose, loading
}) => {
  if (!visible) return null;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-xl border border-gray-700 w-full max-w-3xl max-h-[80vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-700">
          <h3 className="text-white font-semibold text-lg">
            📊 Transactions for {date}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white text-2xl">&times;</button>
        </div>

        {/* Content */}
        <div className="p-4 overflow-y-auto max-h-[60vh]">
          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3].map(i => (
                <div key={i} className="bg-gray-700 rounded-lg p-4 animate-pulse">
                  <div className="h-4 bg-gray-600 rounded w-1/3 mb-2" />
                  <div className="h-3 bg-gray-600 rounded w-1/2" />
                </div>
              ))}
            </div>
          ) : transactions.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No transactions found for this date
            </div>
          ) : (
            <div className="space-y-3">
              {/* Summary */}
              <div className="grid grid-cols-4 gap-3 mb-4">
                {(['bank', 'crypto', 'commission', 'conversion'] as const).map(type => {
                  const filtered = transactions.filter(t => t.type === type);
                  const total = filtered.reduce((s, t) => s + t.shares, 0);
                  return (
                    <div key={type} className={`rounded-lg p-3 ${typeColors[type]}`}>
                      <p className="text-sm opacity-70">{typeLabels[type]}</p>
                      <p className="font-bold">{total.toLocaleString()} shares</p>
                      <p className="text-sm">{filtered.length} txns</p>
                    </div>
                  );
                })}
              </div>

              {/* Transaction List */}
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-white text-left border-b border-gray-700">
                    <th className="pb-2">Type</th>
                    <th className="pb-2">User</th>
                    <th className="pb-2 text-right">Amount</th>
                    <th className="pb-2 text-right">Shares</th>
                    <th className="pb-2 text-right">Time</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((tx) => (
                    <tr key={tx.id} className="border-b border-gray-700/50 hover:bg-gray-700/30">
                      <td className="py-2">
                        <span className={`px-2 py-0.5 rounded text-sm ${typeColors[tx.type]}`}>
                          {typeLabels[tx.type]}
                        </span>
                      </td>
                      <td className="py-2 text-white">{tx.username || tx.userId.slice(0, 8)}...</td>
                      <td className="py-2 text-right text-gray-300">${tx.amount.toLocaleString()}</td>
                      <td className="py-2 text-right text-white font-medium">{tx.shares.toLocaleString()}</td>
                      <td className="py-2 text-right text-gray-500">
                        {new Date(tx.createdAt).toLocaleTimeString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-700 flex justify-between items-center">
          <p className="text-gray-500 text-sm">
            {transactions.length} transaction{transactions.length !== 1 ? 's' : ''}
          </p>
          <button onClick={onClose} className="bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded-lg">
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default DrilldownModal;

